import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { Revision, RevisionSummary, Scene } from "../lib/types.js";
import { Modal } from "../components/Modal.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { formatNumber, formatRelativeTime, revisionReasonLabel } from "../lib/format.js";

interface Props {
  scene: Scene;
  onClose: () => void;
  onRestored: (scene: Scene) => void;
}

/**
 * Version history for a scene: list revisions, preview a chosen one, and
 * restore it. Restoring never overwrites history — the server records the
 * current state and the restore as new revisions.
 */
export function RevisionHistory({ scene, onClose, onRestored }: Props) {
  const toast = useToast();
  const [revisions, setRevisions] = useState<RevisionSummary[] | null>(null);
  const [selected, setSelected] = useState<Revision | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    api
      .listRevisions(scene.id)
      .then((res) => setRevisions(res.revisions))
      .catch((err) => toast.error(err instanceof Error ? err.message : "Failed to load history"));
  }, [scene.id, toast]);

  const preview = async (revisionId: string) => {
    setLoadingPreview(true);
    try {
      const { revision } = await api.getRevision(scene.id, revisionId);
      setSelected(revision);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load revision");
    } finally {
      setLoadingPreview(false);
    }
  };

  const restore = async () => {
    if (!selected) return;
    setRestoring(true);
    try {
      const { scene: restored } = await api.restoreRevision(scene.id, selected.id);
      toast.success("Revision restored as a new version.");
      onRestored(restored);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Modal
      title="Version history"
      subtitle={scene.title}
      onClose={onClose}
      width={860}
      footer={
        <>
          <span className="faint" style={{ fontSize: 12, marginRight: "auto" }}>
            Restoring creates a new version — nothing in your history is lost.
          </span>
          <button className="btn btn-ghost" onClick={onClose} disabled={restoring}>Close</button>
          <button className="btn btn-primary" onClick={restore} disabled={!selected || restoring}>
            <Icon name="history" size={15} /> {restoring ? "Restoring…" : "Restore this version"}
          </button>
        </>
      }
    >
      <div className="history">
        <div className="history-list">
          {revisions === null ? (
            <div className="history-loading"><Icon name="clock" size={18} className="spin" /></div>
          ) : revisions.length === 0 ? (
            <p className="muted" style={{ padding: 12 }}>
              No revisions yet. Keep writing — versions are captured automatically as you work.
            </p>
          ) : (
            revisions.map((rev) => (
              <button
                key={rev.id}
                className={`history-item ${selected?.id === rev.id ? "active" : ""}`}
                onClick={() => preview(rev.id)}
              >
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <span className={`chip ${rev.reason === "restore" ? "chip-accent" : ""}`} style={{ fontSize: 10 }}>
                    {revisionReasonLabel(rev.reason)}
                  </span>
                  <span className="faint" style={{ fontSize: 11 }}>{formatRelativeTime(rev.createdAt)}</span>
                </div>
                <div className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>
                  {formatNumber(rev.wordCount)} words · {new Date(rev.createdAt).toLocaleString()}
                </div>
              </button>
            ))
          )}
        </div>
        <div className="history-preview">
          {loadingPreview ? (
            <div className="history-loading"><Icon name="clock" size={18} className="spin" /></div>
          ) : selected ? (
            <div className="history-preview-content" dangerouslySetInnerHTML={{ __html: selected.content || "<p class='muted'>(empty)</p>" }} />
          ) : (
            <div className="history-empty">
              <Icon name="history" size={28} style={{ color: "var(--text-faint)" }} />
              <p className="muted">Select a version to preview it.</p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
